import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { queryClient } from '../queryClient';
import { createIdempotencyKey } from '../utils/idempotencyKey';

export type FacilityRequestStatus = 'pending' | 'approved' | 'declined' | 'needs_info' | 'cancelled';

export interface FacilityRequest {
  id: string;
  requester_id?: string | null;
  requester_name: string;
  requester_email: string;
  requester_phone?: string | null;
  organization_name?: string | null;
  event_name?: string | null;
  ministry_group?: string | null;
  ministry_group_other?: string | null;
  event_start_time?: string | null;
  event_end_time?: string | null;
  location_of_event?: string[] | null;
  location_other?: string | null;
  event_type?: string | null;
  event_type_other?: string | null;
  description_of_event?: string | null;
  attendee_count?: number | null;
  requested_start_at: string;
  requested_end_at: string;
  message?: string | null;
  table_types?: string[] | null;
  number_of_tables_needed?: number | null;
  food_table_count?: number | null;
  needs_table_linen?: boolean | null;
  has_audio_visual_needs?: boolean | null;
  audio_visual_needs_details?: string | null;
  floor_layout?: string | null;
  floor_layout_image_url?: string | null;
  is_recurring_event?: boolean | null;
  recurrence_frequency?: string | null;
  recurrence_frequency_other?: string | null;
  recurrence_ends_at?: string | null;
  source: 'website' | 'mobile' | 'webapp';
  status: FacilityRequestStatus;
  admin_notes?: string | null;
  admin_response?: string | null;
  responded_by?: string | null;
  responded_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  facility_request_form_responses?: FacilityRequestFormResponse[];
}

export interface FacilityRequestFormResponse {
  id: string;
  request_id: string;
  form_id?: string | null;
  field_id?: string | null;
  field_key: string;
  field_label?: string | null;
  field_type?: string | null;
  answer_value?: unknown;
  created_at?: string | null;
}

export interface FacilityBlackoutDate {
  id: string;
  starts_at: string;
  ends_at: string;
  title: string;
  reason?: string | null;
  location_of_event?: string[] | null;
  location_other?: string | null;
  source: 'manual' | 'google' | 'sync';
  google_event_id?: string | null;
  facilities_group_google_event_id?: string | null;
  all_day: boolean;
  last_synced_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface FacilitySettings {
  id: number;
  facilities_group_id?: string | null;
  google_calendar_id?: string | null;
  facilities_group_calendar_id?: string | null;
  timezone: string;
}

export type FacilityRequestFormFieldType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'checkbox'
  | 'date';

export interface FacilityRequestFormField {
  id?: string;
  form_id?: string;
  field_key: string;
  label: string;
  field_type: FacilityRequestFormFieldType;
  is_required: boolean;
  sort_order: number;
  options?: unknown;
  validation?: unknown;
  visibility_rules?: unknown;
  placeholder?: string | null;
  help_text?: string | null;
}

export interface FacilityRequestForm {
  id?: string;
  form_key: string;
  title: string;
  description?: string | null;
  is_enabled: boolean;
  fields: FacilityRequestFormField[];
}

export const useFacilityRequests = () =>
  useQuery<FacilityRequest[]>({
    queryKey: ['facility-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('facility_requests')
        .select('*, facility_request_form_responses(*)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as FacilityRequest[];
    },
  });

export const useUpdateFacilityRequest = () =>
  useMutation({
    mutationFn: async (payload: {
      id: string;
      status?: FacilityRequestStatus;
      adminNotes?: string | null;
      adminResponse?: string | null;
    }) => {
      const idempotencyKey = createIdempotencyKey('web-facility-request-update', payload);
      const { data, error } = await supabase.functions.invoke('update-facility-request', {
        body: {
          ...payload,
          idempotencyKey,
        },
        headers: {
          'X-Idempotency-Key': idempotencyKey,
        },
      });
      if (error) throw error;
      if (data && (data as { error?: string }).error) throw new Error((data as { error: string }).error);
      return data as { success: boolean; data: FacilityRequest };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
    },
  });

export const useDeleteFacilityRequest = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('facility_requests').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
    },
  });

export const useFacilityBlackoutDates = () =>
  useQuery<FacilityBlackoutDate[]>({
    queryKey: ['facility-blackout-dates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('facility_blackout_dates')
        .select('*')
        .order('starts_at', { ascending: true });
      if (error) throw error;
      return (data || []) as FacilityBlackoutDate[];
    },
  });

export const useCreateFacilityBlackoutDate = () =>
  useMutation({
    mutationFn: async (payload: {
      starts_at: string;
      ends_at: string;
      title?: string;
      reason?: string | null;
      location_of_event?: string[] | null;
      location_other?: string | null;
      all_day?: boolean;
    }) => {
      const { data: authData } = await supabase.auth.getUser();
      const actorId = authData?.user?.id ?? null;
      const { data, error } = await supabase
        .from('facility_blackout_dates')
        .insert({
          ...payload,
          source: 'manual',
          title: payload.title?.trim() || 'Facility Blackout',
          created_by: actorId,
          updated_by: actorId,
        })
        .select('*')
        .single();
      if (error) throw error;

      const { data: syncData, error: syncError } = await supabase.functions.invoke('sync-facility-calendar', {
        body: {},
      });
      if (syncError) {
        throw new Error(`Blackout saved, but Google Calendar sync failed: ${syncError.message}`);
      }
      if (syncData && (syncData as { error?: string }).error) {
        throw new Error(`Blackout saved, but Google Calendar sync failed: ${(syncData as { error: string }).error}`);
      }

      return data as FacilityBlackoutDate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-blackout-dates'] });
    },
  });

export const useDeleteFacilityBlackoutDate = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('facility_blackout_dates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-blackout-dates'] });
    },
  });

export const useFacilitySettings = () =>
  useQuery<FacilitySettings | null>({
    queryKey: ['facility-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('facility_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return (data as FacilitySettings | null) ?? null;
    },
  });

export const useUpdateFacilitySettings = () =>
  useMutation({
    mutationFn: async (payload: Partial<FacilitySettings>) => {
      const { data, error } = await supabase
        .from('facility_settings')
        .update(payload)
        .eq('id', 1)
        .select('*')
        .single();
      if (error) throw error;
      return data as FacilitySettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-settings'] });
    },
  });

export const useSyncFacilityCalendar = () =>
  useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-facility-calendar', { body: {} });
      if (error) throw error;
      if (data && (data as { error?: string }).error) throw new Error((data as { error: string }).error);
      return data as {
        success: boolean;
        data: {
          pushedToGoogle: number;
          pulledFromGoogle: number;
          removedCancelled: number;
          syncedAt: string;
        };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-blackout-dates'] });
    },
  });

export const useFacilityRequestForm = () =>
  useQuery<FacilityRequestForm | null>({
    queryKey: ['facility-request-form'],
    queryFn: async () => {
      const { data: form, error: formError } = await supabase
        .from('facility_request_forms')
        .select('*')
        .eq('form_key', 'facility_booking')
        .maybeSingle();
      if (formError) throw formError;
      if (!form) return null;

      const { data: fields, error: fieldsError } = await supabase
        .from('facility_request_form_fields')
        .select('*')
        .eq('form_id', form.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (fieldsError) throw fieldsError;

      return {
        ...(form as Omit<FacilityRequestForm, 'fields'>),
        fields: (fields as FacilityRequestFormField[]) ?? [],
      };
    },
  });

export const useSaveFacilityRequestForm = () =>
  useMutation({
    mutationFn: async (payload: FacilityRequestForm) => {
      const formKey = payload.form_key?.trim() || 'facility_booking';
      const { data: form, error: formError } = await supabase
        .from('facility_request_forms')
        .upsert(
          {
            form_key: formKey,
            title: payload.title?.trim() || 'Facility Booking Additional Questions',
            description: payload.description?.trim() || null,
            is_enabled: Boolean(payload.is_enabled),
          },
          { onConflict: 'form_key' }
        )
        .select('*')
        .single();
      if (formError) throw formError;

      const { error: deleteError } = await supabase
        .from('facility_request_form_fields')
        .delete()
        .eq('form_id', form.id);
      if (deleteError) throw deleteError;

      if (!payload.fields || payload.fields.length === 0) {
        return { ...form, fields: [] } as FacilityRequestForm;
      }

      const rows = payload.fields.map((field, index) => ({
        form_id: form.id,
        field_key: field.field_key,
        label: field.label,
        field_type: field.field_type,
        is_required: Boolean(field.is_required),
        sort_order: Number.isFinite(field.sort_order) ? field.sort_order : index,
        options: field.options ?? [],
        validation: field.validation ?? {},
        visibility_rules: field.visibility_rules ?? {},
        placeholder: field.placeholder ?? null,
        help_text: field.help_text ?? null,
      }));

      const { data: fields, error: fieldsError } = await supabase
        .from('facility_request_form_fields')
        .insert(rows)
        .select('*');
      if (fieldsError) throw fieldsError;

      return {
        ...(form as Omit<FacilityRequestForm, 'fields'>),
        fields: (fields as FacilityRequestFormField[]) ?? [],
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-request-form'] });
    },
  });
